import React, { useState, useEffect, useCallback } from 'react';

import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Globe, ImageIcon } from 'lucide-react';
import './AdminPanel.css';

const PAGES = [
    // Main Pages
    // Main Pages
    {
        key: 'home',
        name: 'Home',
        path: '/',
        defaultTitle: 'BPO Consultants | Free Global Staffing Strategy Session',
        defaultDescription: 'Unlock global talent and save 70% with our AI-augmented BPO strategy. Book your free consultation today.',
        defaultContent: `<h1>Smart Businesses Plan Before They Offshore.</h1>
<p>Most BPO strategies fail because they buy "staff" instead of building a "strategy." We don't just fill seats. We build your global growth engine.</p>

<h2>Why 90% of Offshoring Attempts Fail to Scale</h2>
<p>The problem isn't the people. The problem is the model. Traditional agencies treat humans like hardware—plug and play. Real success requires a strategic partnership that integrates culture, AI, and workflow.</p>

<h3>The 3 Pillars of BPO Success</h3>
<ul>
    <li>1. Elite Talent Acquisition (Top 1%)</li>
    <li>2. [HIDDEN] - The Secret to Retention</li>
    <li>3. AI-Augmented Workflow</li>
</ul>

<h2>The BPO Consultants Edge</h2>
<ul>
    <li>70%+ Cost Reduction (Optimized)</li>
    <li>AI-Augmented Productivity (2x Output)</li>
    <li>Cultural & Operational Integration</li>
    <li>Retention-First Architecture</li>
    <li>Global Talent, Local Mindset</li>
</ul>

<h2>Your Path to Global Expansion</h2>
<p>It starts with a conversation, not a contract.</p>
<ol>
    <li><strong>Free Discovery:</strong> We analyze your current workflow, identify bottlenecks, and calculate your exact potential savings.</li>
    <li><strong>Strategic Design:</strong> We build a custom org chart and tech stack (including AI tools) tailored to your industry.</li>
    <li><strong>Execution:</strong> We recruit, vet, and onboard your elite team, ensuring day-1 cultural alignment.</li>
</ol>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "BPO Consultants Philippines",
            "url": "https://bpoconsultants.ph",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://bpoconsultants.ph/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        }, null, 4)
    },
    {
        key: 'about',
        name: 'About Us',
        path: '/about',
        defaultTitle: 'About BPO Consultants | Our Story & Mission',
        defaultDescription: 'We are not just another BPO. We are your offshore strategy partners. Learn about our mission to democratize global talent for businesses of all sizes.',
        defaultContent: `<h1>Democratizing Global Talent</h1>
<p>We believe that every business, regardless of size, deserves access to the world's best talent. We are bridging the gap between ambition and execution.</p>

<h2>Our Story</h2>
<p>BPO Consultants was born from a frustration with the traditional outsourcing model. Heavy fees, opaque pricing, and a "body shop" mentality that treated talent like commodities.</p>
<p>We set out to build something different. A firm that acts as a strategic partner, not just a vendor. We focus on integration, not just delegation. We help you build teams that feel like they are sitting in the room next to you, even if they are 8,000 miles away.</p>

<ul>
    <li>Transparent, flat-rate pricing</li>
    <li>Top 1% talent selection process</li>
    <li>Dedicated operational support</li>
</ul>

<h2>Why The Philippines?</h2>
<p>It's not just about cost. It's about culture, capability, and compatibility.</p>
<ul>
    <li><strong>Cultural Affinity:</strong> English is an official language. The work culture is heavily influenced by the West, ensuring seamless communication.</li>
    <li><strong>Highly Educated:</strong> The country produces over 600,000 college graduates annually, specializing in IT, Engineering, Healthcare, and Finance.</li>
    <li><strong>Service Oriented:</strong> Filipinos are world-renowned for their empathy and hospitality, making them exceptional at customer support and care.</li>
</ul>

<h2>Our DNA</h2>
<p><strong>Excellence over Volume:</strong> We would rather staff one perfect team member than ten mediocre ones. We are distinctively picky in our recruitment process.</p>
<p><strong>People First:</strong> We take care of our people so they take care of your business. Competitive pay, HMO, and career growth are non-negotiable.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
                "@type": "Organization",
                "name": "BPO Consultants",
                "url": "https://bpoconsultants.ph",
                "description": "Empowering businesses with elite offshore talent and strategic AI integration."
            }
        }, null, 4)
    },
    {
        key: 'contact',
        name: 'Contact Us',
        path: '/contact',
        defaultTitle: 'Contact Us | Free BPO Assessment',
        defaultDescription: 'Get in touch for a free AI-powered BPO assessment. Contact our team in Manila or book a consultation today.',
        defaultContent: `<h1>Let's Build Your Team</h1>
<p>Ready to reduce costs and scale up? Get your free expert assessment today.</p>

<h2>Get Personalized Offshore Staffing Advice</h2>
<p>Book a direct 15-minute strategy call with our solutions architect. No sales pressure, just answers.</p>

<h2>Contact Information</h2>
<p><strong>Email Us:</strong> info@bpoconsultants.ph</p>
<p><strong>Call Us:</strong> +63 (2) 8XXX XXXX (Mon-Fri 8am-5pm GMT+8)</p>
<p><strong>Headquarters:</strong> Bonifacio Global City, Taguig, Metro Manila, Philippines</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
                "@type": "Organization",
                "name": "BPO Consultants",
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Taguig",
                    "addressRegion": "Metro Manila",
                    "addressCountry": "PH"
                },
                "contactPoint": {
                    "@type": "ContactPoint",
                    "email": "info@bpoconsultants.ph",
                    "contactType": "customer support"
                }
            }
        }, null, 4)
    },
    {
        key: 'careers',
        name: 'Careers',
        path: '/careers',
        defaultTitle: 'Careers at BPO Consultants | Join Our Team',
        defaultDescription: 'Build the future of AI-augmented outsourcing. Explore career opportunities with BPO Consultants Philippines.',
        defaultContent: `<h1>Build the Future of AI-Augmented Work</h1>
<p>Join a team that's redefining the BPO industry. We combine human empathy with machine intelligence to deliver world-class experiences.</p>

<h2>Why Join Us?</h2>
<ul>
    <li><strong>Culture First:</strong> We believe happy employees deliver the best results. We prioritize work-life balance, mental health, and professional growth.</li>
    <li><strong>Cutting-Edge Tech:</strong> Don't get stuck with legacy systems. Work with the latest AI tools, automation platforms, and cloud technologies.</li>
    <li><strong>Global Impact:</strong> Work with exciting startups and Fortune 500 companies from around the world, right from the Philippines.</li>
</ul>

<h2>Open Positions</h2>
<p>Don't see a perfect fit? We're always looking for talented individuals. Send your resume to our HR team and we'll keep you on file: info@bpoconsultants.ph</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Careers at BPO Consultants",
            "description": "Join our team and build the future of AI-augmented outsourcing.",
            "url": "https://bpoconsultants.ph/careers"
        }, null, 4)
    },

    // Solutions (was Services)
    {
        key: 'solutions_ai',
        name: 'Solutions: AI-Augmented Staffing',
        path: '/services/ai-staffing',
        defaultTitle: 'AI-Augmented Staffing Solutions | Elite Talent + Advanced Automation',
        defaultDescription: 'Revolutionize your workforce with AI-augmented staffing. Combine top 1% Filipino talent with cutting-edge AI tools to boost productivity by 200% and cut costs by 70%.',
        defaultContent: `<h1>AI-Augmented Staffing: The Future of Offshore Teams</h1>
<p>Don't just hire people. Hire a super-powered workforce equipped with the latest AI tools to deliver 2x the output at half the cost.</p>

<h2>The Old BPO Model is Dead. Welcome to Staffing 2.0.</h2>
<p>For decades, the BPO industry has sold you on one thing: cheap labor. You hire a remote employee, they do the work, you save some money. But in 2026, 'cheap' isn't enough. You need *efficient*. You need *innovative*. You need *speed*.</p>
<p>Traditional offshoring is plagued by inefficiencies. Manual data entry, repetitive tasks, and communication gaps slow down your operations. AI-Augmented Staffing changes the game by equipping elite offshore talent with a custom stack of AI tools—Generative AI, Automation Agents, and Intelligent Analytics—empowering one person to do the work of three.</p>

<h2>Why AI-Augmented Staffing?</h2>
<ul>
    <li><strong>200% Productivity Boost:</strong> Our staff use AI co-pilots for coding, writing, and data analysis, drastically reducing turnaround times.</li>
    <li><strong>70% Cost Reduction:</strong> Save on salaries AND software licenses. We provide the talent and the tech stack integration.</li>
    <li><strong>Zero Hallucination Policy:</strong> We train our staff on 'Human-in-the-Loop' protocols, ensuring AI output is verified for accuracy and compliance.</li>
    <li><strong>Custom AI Tooling:</strong> We don't just give them ChatGPT. We build custom workflows using tools like Zapier, Make, and proprietary agents.</li>
</ul>

<h2>How We Integrate AI into Every Role</h2>
<h3>Customer Support & CX</h3>
<p>Forget scripted robots. Our CX agents use AI to analyze customer sentiment in real-time, suggest hyper-personalized responses, and automate ticket tagging. This results in a 40% reduction in average handling time (AHT) and a CSAT score that rivals onshore teams.</p>

<h3>Virtual Assistants & Admin</h3>
<p>A traditional VA manages your calendar. An AI-Augmented VA automates your entire inbox, drafts complex reports using predictive text, and manages data entry with OCR technology. They don't just follow instructions; they optimize workflows.</p>

<h3>Digital Marketing & Content</h3>
<p>Our content creators harness LLMs to generate high-quality drafts, which they then refine with human creativity and cultural nuance. They use AI for SEO analysis, competitor tracking, and automated social media scheduling, delivering the output of a full agency.</p>

<h3>Software Development</h3>
<p>Our developers are pair-programmed with AI coding assistants (GitHub Copilot, Cursor). This leads to faster code generation, automated unit testing, and cleaner documentation. You get robust, scalable code in a fraction of the time.</p>

<h2>The 'Human-in-the-Loop' Advantage</h2>
<p>Many business owners fear that AI will replace quality with generic, error-prone output. We solve this with our rigorous 'Human-in-the-Loop' (HITL) methodology.</p>
<p>AI generates the draft, the code, or the data. The Human—your dedicated BPO Consultant—verifies, refines, and finalizes it. This hybrid approach ensures you get the speed of automation with the judgment, empathy, and creativity of a top-tier professional. It allows your offshore team to punch above their weight class, delivering results that formerly required expensive senior hires.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI-Augmented Staffing",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            },
            "description": "Elite offshore talent integrated with advanced AI tools for maximum productivity."
        }, null, 4)
    },
    {
        key: 'solutions_cost',
        name: 'Solutions: Cost Optimization',
        path: '/services/cost-optimization',
        defaultTitle: 'Strategic Cost Optimization | Save 70% Without Sacrificing Quality',
        defaultDescription: "Maximize your runway with strategic BPO cost optimization. We don't just cut costs; we optimize value through labor arbitrage, process efficiency, and tax incentives.",
        defaultContent: `<h1>Smart Cost Optimization: Stop Bleeding Cash</h1>
<p>Turn your biggest expense (payroll) into your biggest competitive advantage using Global Labor Arbitrage.</p>

<h2>The 'Race to the Bottom' is a Trap</h2>
<p>Most companies approach cost cutting the wrong way. They hire the cheapest freelancers they can find, only to pay double in management time, errors, and rework. That isn't optimization; it's self-sabotage.</p>
<p>True Cost Optimization isn't about paying people less—it's about paying the *right* price in the *right* market for the *best* talent. By leveraging the Philippines' economic structure, you can hire a Senior Data Analyst for the price of a Junior Intern in the US, while actually offering them a top-tier local salary. This creates loyalty, quality, and massive savings simultaneously.</p>

<h2>Where We Find the Money</h2>
<ul>
    <li><strong>Labor Arbitrage:</strong> Access senior-level talent at 30% of Western market rates due to cost-of-living differences.</li>
    <li><strong>Operational Efficiency:</strong> Streamline workflows to remove bottlenecks, reducing the 'time-cost' of every task.</li>
    <li><strong>Infrastructure Savings:</strong> No need for local office space, equipment, or benefits packages. We handle the overhead.</li>
    <li><strong>Tech Stack Consolidation:</strong> We audit your software licenses and replace expensive enterprise seats with efficient team accounts.</li>
</ul>

<h2>Our 3-Phase Optimization Framework</h2>
<h3>Phase 1: The 'Waste Audit'</h3>
<p>We don't just look at salaries. We analyze your entire operational workflow. We identify tasks that are highly repetitive, low-value for your core team, but high-cost in terms of time. These are the first targets for offshoring or automation.</p>

<h3>Phase 2: The 'Plug-and-Play' Transfer</h3>
<p>We document your processes into Standard Operating Procedures (SOPs). This ensures that when we move a function offshore, knowledge isn't lost. We create a 'Digital Twin' of your local workflow, ensuring the offshore team steps into a structured environment, not a chaotic one.</p>

<h3>Phase 3: Continuous Improvement Loop</h3>
<p>Cost optimization isn't a one-time event. Our teams are incentivized to find faster ways to do their jobs. We implement quarterly reviews where we re-assess tools and workflows, often finding an additional 10-15% efficiency gain year-over-year.</p>

<h2>The ROI of 'Quality' Over 'Cheap'</h2>
<p>Let's do the math.</p>
<p><strong>Option A (Cheap):</strong> Hire a $5/hr freelancer. They require 10 hours of your management/week. They make errors that delay launch by 2 weeks. Total Cost: Low salary, but massive Opportunity Cost.</p>
<p><strong>Option B (Strategic):</strong> Hire a $12/hr Senior Offshore Pro. They require 1 hour of management/week. They improve your process. You launch on time.</p>
<p>We focus exclusively on Option B. We target the top 10% of the talent pool, pay them above-market rates to ensure 98% retention, and STILL save you 60-70% compared to onshore hires. This is the difference between 'outsourcing' and 'optimizing'.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Cost Optimization",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'solutions_strategy',
        name: 'Solutions: Offshore Strategy',
        path: '/services/offshore-strategy',
        defaultTitle: 'Offshore Strategy Consulting | Build Your Global Org Chart',
        defaultDescription: 'Offshoring fails without a strategy. We design your global organization chart, ensuring cultural alignment, legal compliance, and operational scalability.',
        defaultContent: `< h1 > Offshore Strategy: Blueprint for Expansion</h1 >
<p>Stop treating offshoring like a gig. Start treating it like an acquisition. We build your satellite office in the cloud.</p>

<h2>You Don't Need 'Helpers'. You Need a Department.</h2>
<p>The biggest mistake business owners make is hiring one-off virtual assistants to 'put out fires.' This creates a fragmented, chaotic team structure giving you more people to manage, not less.</p>
<p>Our Offshore Strategy service builds a cohesive extension of your company. We design an org chart with team leads, QA specialists, and individual contributors. We establish reporting lines, KPIs, and cultural rituals that make your offshore team feel just as part of the vision as your local team.</p>

<h2>Strategic Pillars</h2>
<ul>
    <li><strong>Cultural Integration:</strong> We bridge the gap between Western business expectations and Filipino service culture.</li>
    <li><strong>Org Chart Design:</strong> We structure your team with clear hierarchy, career paths, and succession planning.</li>
    <li><strong>Risk Mitigation:</strong> We handle data privacy (GDPR/Data Privacy Act), contracts, and redundant internet/power.</li>
    <li><strong>Knowledge Management:</strong> We build your internal Wiki/Knowledge Base so your business IP is documented and secure.</li>
</ul>

<h2>The 'Satellite Office' Mindset</h2>
<h3>Step 1: Role Decomposition</h3>
<p>We analyze your onshore senior staff. What are they spending their time on that isn't their 'Zone of Genius'? We strip those tasks away to create clear, high-impact offshore job descriptions.</p>

<h3>Step 2: The 'Bridge' Hire</h3>
<p>We often recommend starting with a Team Lead or 'Bridge' Manager. This person acts as the conduit between your local leadership and the broader offshore team, ensuring you have a single point of accountability.</p>

<h3>Step 3: The Operational Rhythm</h3>
<p>We install your meeting cadence. Daily standups, weekly scorecards, monthly retrospectives. We ensure the offshore team runs on *your* heartbeat, not in a silo.</p>

<h2>Why Culture Eats Strategy for Breakfast</h2>
<p>In the Philippines, 'Malasakit' (genuine care/concern) is a core cultural value. If you treat your offshore team like transactional vendors, you get transactional results.</p>
<p>Our strategy focuses heavily on unlocking 'Malasakit'. We teach you how to onboard, how to give feedback, and how to recognize wins in a way that resonates culturally. When you get this right, you don't just get employees; you get brand evangelists who will work tirelessly for your success. We provide the playbook for this cultural calibration.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Offshore Strategy Consulting",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            },
            "description": "Strategic blueprinting for global organizational expansion and satellite office setup."
        }, null, 4)
    },
    {
        key: 'solutions_dt',
        name: 'Solutions: Digital Transformation',
        path: '/services/digital-transformation',
        defaultTitle: 'Digital Transformation | Modernize Before You Scale',
        defaultDescription: 'Don\'t offshore a mess. We modernize your legacy workflows with low-code automation and cloud integration before we build your team.',
        defaultContent: `< h1 > Digital Transformation</h1 >
<p>Fix your broken processes before you scale them. We digitize your workflow to make it offshore-ready.</p>

<h2>Automate First, Delegate Second</h2>
<p>Many businesses try to solve efficiency problems by throwing more people at them. If your process is broken, hiring more people just makes the chaos faster.</p>
<p>Our Digital Transformation service focuses on 'Pre-Offshoring Hygiene'. We audit your tech stack, kill legacy spreadsheets, and implement modern project management tools (ClickUp, Asana, Monday). We build the digital infrastructure that allows a remote team to plug in and be productive from Day 1. We believe you should never pay a human to do what a robot can do for free.</p>

<h2>Transformation Outcomes</h2>
<ul>
    <li><strong>Workflow Automation:</strong> We use Zapier/Make to connect your apps, eliminating manual data transfer.</li>
    <li><strong>Data Centralization:</strong> Move from scattered Excel sheets to a unified Cloud CRM/Database source of truth.</li>
    <li><strong>Security Protocols:</strong> Implement LastPass/1Password and 2FA, securing your access points before sharing credentials.</li>
    <li><strong>Async Communication:</strong> Setup Slack/Teams channels and protocols to reduce meetings by 40%.</li>
</ul>

<h2>Our Modernization Toolkit</h2>
<h3>The 'No-Code' Revolution</h3>
<p>You don't need a $50k custom software build. We utilize powerful no-code tools like Airtable and Softr to build custom internal portals for your operations. This gives you enterprise-grade software at startup prices.</p>

<h3>CRM Implementation</h3>
<p>Your customer data is your gold mine. We clean, migrate, and structure your HubSpot, Salesforce, or Pipedrive instance to ensure that when your offshore sales support team starts, they have clean data to work with.</p>

<h3>SOP Digitization</h3>
<p>Static PDFs get ignored. We build interactive SOPs using tools like Loom and Guru. We create a searchable, video-first training library that enables self-serve onboarding for new hires.</p>

<h2>The 'Digital Twin' Concept</h2>
<p>Before we hire a single person, we aim to build a 'Digital Twin' of your operation. This means every process has a digital trigger and action.</p>
<p>For example, instead of emailing a task to an employee, a form submission in your website triggers a notification in Slack, creates a card in Trello, and auto-populates a draft email in Gmail. The human only steps in to review and hit send. This dramatically lowers the skill floor required for the role, allowing you to hire for attitude and reliability rather than obscure technical experience.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Digital Transformation",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },

    // Industries
    {
        key: 'industries',
        name: 'Industries (Main)',
        path: '/industries',
        defaultTitle: 'Industries | BPO Consultants',
        defaultDescription: 'Explore our specialized BPO solutions across various industries.',
        defaultContent: `< h1 > Industries We Serve</h1 > <p>We provide specialized BPO solutions tailored to the unique needs of various industries. From healthcare to technology, our experts understand your sector.</p>`
    },
    {
        key: 'industry_technology',
        name: 'Industries: Technology & IT',
        path: '/industries/technology',
        defaultTitle: 'Offshore IT & Tech Staffing | Top 1% Filipino Developers',
        defaultDescription: 'Scale your dev team with elite Filipino talent. From Full-Stack Developers to QA Engineers, we provide AI-augmented tech staffing at 70% less cost.',
        defaultContent: `< h1 > Scale Your Dev Team Without the Silicon Valley Price Tag</h1 >
<p><strong>Access the top 1% of Filipino technical talent. Pre-vetted, AI-enabled, and ready to deploy in your time zone.</strong></p>

<h2>The Tech Talent Shortage is a Myth (If You Look Globally)</h2>
<p>Local hiring is broken. You're fighting with Google and Meta for the same pool of expensive developers. Meanwhile, thousands of senior-level engineers in the Philippines are looking for opportunities to build world-class software.</p>
<p>We specialize in finding 'Unicorn' talent—developers who have excellent English, deep technical expertise, and the ability to work autonomously. We don't just fill seats; we build extended engineering pods that integrate directly into your Agile/Scrum sprints.</p>

<h2>Why Offshore IT with Us?</h2>
<ul>
    <li><strong>Full-Stack Expertise:</strong> React, Node, Python, AWS. Our talent pool covers the modern tech stack.</li>
    <li><strong>IP Protection:</strong> Bank-grade security protocols. Your code lives on your servers, not ours.</li>
    <li><strong>DevOps & QA:</strong> Don't just hire coders. Hire the infrastructure pros that keep your app up.</li>
    <li><strong>AI-Augmented Coding:</strong> All our devs use Copilot/Cursor to write cleaner code 2x faster.</li>
</ul>

<h2>Roles We Fill Instantly</h2>
<h3>Software Engineering</h3>
<p>Frontend (React/Vue), Backend (Node/Python/Go), and Mobile (React Native/Flutter). We test for algorithmic thinking, not just syntax memorization.</p>

<h3>Data Science & AI</h3>
<p>Hire Data Analysts to clean your records or AI Engineers to fine-tune your LLMs. The Philippines has a booming data science community ready to tackle complex problems.</p>

<h3>Technical Support (L2/L3)</h3>
<p>Move your Tier 2 and Tier 3 support offshore. Our engineers can debug code, run SQL queries, and resolve tickets while your local team sleeps.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "IT & Tech Staffing",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'industry_healthcare',
        name: 'Industries: Healthcare',
        path: '/industries/healthcare',
        defaultTitle: 'Healthcare BPO & Medical Staffing | HIPAA Compliant',
        defaultDescription: 'Reduce administrative burnout with HIPAA-compliant medical virtual assistants. We support clinics, telehealth, and insurance providers.',
        defaultContent: `< h1 > Focus on Patients, Not Paperwork</h1 >
<p><strong>HIPAA-compliant Medical Virtual Assistants (MVAs) and Registered Nurses (RNs) to handle your admin, billing, and triage.</strong></p>

<h2>The Cure for Administrative Burnout</h2>
<p>Doctors and nurses didn't go to med school to fill out forms. Yet, 40% of their time is spent on EHR data entry and insurance verifications.</p>
<p>Our Healthcare BPO solutions provide you with clinically trained staff—often Registered Nurses (RNs) in the Philippines—who act as virtual scribes, intake coordinators, and billing specialists. Because they handle the admin, your local providers can see more patients and provide better care.</p>

<h2>Clinical Excellence, Offshore Costs</h2>
<ul>
    <li><strong>Registered Nurses:</strong> Hire Philippine RNs to do non-patient facing clinical work for $12-15/hr.</li>
    <li><strong>HIPAA Certified:</strong> Every staff member undergoes rigorous HIPAA compliance training and background checks.</li>
    <li><strong>Revenue Cycle:</strong> accelerated billing and collections. We fight denials so you get paid faster.</li>
    <li><strong>Patient Empathy:</strong> Filipino culture is world-renowned for care and empathy, improving patient satisfaction.</li>
</ul>

<h2>Specialized Medical Roles</h2>
<h3>Medical Virtual Assistant (MVA)</h3>
<p>Handles patient scheduling, prior authorizations, prescription refills, and referral coordination. They live inside your EHR system.</p>

<h3>Virtual Scribe</h3>
<p>Connects via audio during patient visits to document SOAP notes in real-time. This saves physicians 2-3 hours of charting per day.</p>

<h3>Telehealth Triage</h3>
<p>RNs who answer patient calls, assess symptoms using your protocols, and route them to the appropriate level of care.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Healthcare BPO",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'industry_retail',
        name: 'Industries: Retail',
        path: '/industries/retail',
        defaultTitle: 'E-commerce & Retail BPO | 24/7 CS & Operations',
        defaultDescription: 'Scale your Shopify/Amazon store with 24/7 customer support and inventory management. Reduce returns and boost LTV with our e-commerce specialists.',
        defaultContent: `< h1 > Scale Your Store, Not Your Stress</h1 >
<p><strong>Stop answering support tickets at 2 AM. Build a 24/7 engine for Customer Experience (CX) and Backend Operations.</strong></p>

<h2>The 'Always-On' Customer</h2>
<p>Your customers shop 24/7. If you're not there to answer their questions instantly, they bounce to a competitor. But hiring local support for night shifts is expensive and hard to manage.</p>
<p>We build dedicated 24/7 support teams for DTC brands and retailers. Our agents don't just 'resolve tickets'; they are trained in consultative selling, turning inquiries into upsells. They also handle the unglamorous backend work—order processing, refunds, and chargeback disputes—so you can focus on product and brand.</p>

<h2>E-commerce Efficiency</h2>
<ul>
    <li><strong>Omnichannel Support:</strong> We manage Email, Live Chat, SMS, and Social Media DMs in a unified inbox.</li>
    <li><strong>Revenue Recovery:</strong> Agents pro-actively reach out to abandoned carts and failed payments to save sales.</li>
    <li><strong>Fraud Prevention:</strong> Dedicated teams review high-risk orders to prevent chargebacks before they happen.</li>
    <li><strong>Peak Scalability:</strong> Ramp up your team size by 300% for Black Friday/Cyber Monday, then scale back down.</li>
</ul>

<h2>Roles That Drive GMV</h2>
<h3>Customer Success Specialist</h3>
<p>Far beyond a call center rep. They know your product catalog inside out, offering sizing advice and product recommendations that increase Average Order Value (AOV).</p>

<h3>E-commerce Virtual Assistant</h3>
<p>Handles inventory management, supplier coordination, and product listing optimization (SEO titles, image alt tags) on Shopify, Amazon, and Walmart.</p>

<h3>Influencer Manager</h3>
<p>Scouts, contacts, and manages relationships with hundreds of micro-influencers to drive organic traffic and UGC (User Generated Content) for your brand.</p>`
    },
    {
        key: 'industry_finance',
        name: 'Industries: Finance',
        path: '/industries/finance',
        defaultTitle: 'Financial Services BPO | Accounting & Fintech Support',
        defaultDescription: 'Secure, CPAs and financial analysts for accounting firms and fintechs. Save 70% on bookkeeping, tax prep, and financial analysis.',
        defaultContent: `< h1 > Financial Excellence, Global Efficiency</h1 >
<p><strong>Expand your margin by hiring CPA-qualified accountants and financial analysts in the Philippines.</strong></p>

<h2>The Talent Crunch in Accounting</h2>
<p>The US is facing a massive shortage of accountants (the 'CPA Crisis'). Firms are turning away work because they literally cannot find staff to do it.</p>
<p>The Philippines produces tens of thousands of CPA-qualified graduates every year who are fluent in US GAAP, IFRS, and Australian tax laws. They are proficient in Xero, QuickBooks, NetSuite, and Excel. We help accounting firms and fintechs build dedicated offshore teams to handle the grunt work, allowing local partners to focus on advisory and client relationships.</p>

<h2>Bank-Grade Security & Compliance</h2>
<ul>
    <li><strong>Data Security:</strong> ISO 27001 compliant environments with disabled USB ports, clean desk policies, and VPNs.</li>
    <li><strong>Cost Efficiency:</strong> Hire a Senior Accountant for the cost of a local admin assistant.</li>
    <li><strong>Accuracy:</strong> Multi-tier review processes ensure books are closed with 99.9% accuracy.</li>
    <li><strong>Fintech Support:</strong> KYC/AML analysts to process verification documents for crypto and fintech apps.</li>
</ul>

<h2>Specialized Financial Roles</h2>
<h3>Staff Accountant / Bookkeeper</h3>
<p>Manages AP/AR, bank reconciliations, payroll processing, and month-end closing procedures.</p>

<h3>Tax Preparer</h3>
<p>Prepares individual (1040) and corporate (1120) tax returns for review by US CPAs. Familiar with CCH Axcess, Lacerte, and Drake.</p>

<h3>Financial Analyst</h3>
<p>Builds financial models, tracks KPIs, and creates investor presentations for CFOs and Private Equity firms.</p>`
    },
    {
        key: 'industry_saas',
        name: 'Industries: SaaS',
        path: '/industries/saas',
        defaultTitle: 'SaaS Operations & Support | L1/L2 Technical Support',
        defaultDescription: 'Hyper-growth support for SaaS companies. From technical onboarding to churn reduction, we help you scale your ARR.',
        defaultContent: `< h1 > Scale Your SaaS Without Bloating Your Burn Rate</h1 >
<p><strong>Customer Success, Technical Support, and Sales Development Reps (SDRs) who understand software.</strong></p>

<h2>Support is Your New Sales Channel</h2>
<p>In SaaS, Net Dollar Retention (NDR) is everything. If your users can't figure out your product, they churn. If they have to wait 24 hours for a reply, they churn.</p>
<p>We provide technical support agents who are actual power users. They don't just read scripts; they troubleshoot APIs, debug integrations, and walk users through complex workflows via Zoom. They are your first line of defense against churn and your best source of product feedback.</p>

<h2>Growth-Engine Operations</h2>
<ul>
    <li><strong>Technical Aptitude:</strong> We hire agents with CS degrees who understand APIs, webhooks, and JSON.</li>
    <li><strong>Churn Reduction:</strong> Pro-active Customer Success Managers (CSMs) who monitor health scores and intervene.</li>
    <li><strong>24/7 Coverage:</strong> Support your global user base across all time zones.</li>
    <li><strong>SDR/BDR Teams:</strong> Outbound sales teams to prospect, qualify, and book demos for your Account Executives.</li>
</ul>

<h2>Roles for Hyper-Growth</h2>
<h3>Technical Support Engineer (L1/L2)</h3>
<p>Handles technical tickets, replicates bugs, and escalates directly to your engineering team via Jira/Linear.</p>

<h3>Customer Onboarding Specialist</h3>
<p>Guides new enterprise clients through setup, data migration, and training to ensure 'Time to Value' is minimized.</p>

<h3>Sales Development Rep (SDR)</h3>
<p>High-volume prospectors who use Apollo/ZoomInfo to find leads and run cold email/call sequences to fill your pipeline.</p>`
    },
    {
        key: 'industry_construction',
        name: 'Industries: Construction',
        path: '/industries/construction',
        defaultTitle: 'Construction BPO | Remote Estimators & BIM',
        defaultDescription: 'Scale your construction firm with offshore estimators, quantity surveyors, and BIM modelers. Save 60% on pre-construction costs.',
        defaultContent: `< h1 > Build More, Bid More</h1 >
<p><strong>Win more projects with a dedicated team of offshore estimators and BIM specialists.</strong></p>

<h2>Pre-Construction at Scale</h2>
<p>The bottleneck in construction isn't always on the job site; it's in the back office. Bidding takes time. takeoff takes time. Drafting takes time.</p>
<p>We provide civil engineers and architects in the Philippines who specialize in US/UK building codes. They handle the tedious pre-construction work—quantity takeoffs, estimation, and 3D modeling—so your local team can focus on project management and client relationships.</p>

<h2>Engineering Support</h2>
<ul>
    <li><strong>Estimating Teams:</strong> Dedicated teams using Bluebeam, PlanSwift, and RSMeans to crank out bids.</li>
    <li><strong>CAD & BIM:</strong> Revit, AutoCAD, and Civil 3D experts for drafting and modeling.</li>
    <li><strong>Project Admin:</strong> Handle RFI logs, submittals, and change orders remotely.</li>
    <li><strong>24/7 Operations:</strong> Your offshore team preps the work while you sleep, ready for review by 8 AM.</li>
</ul>

<h2>Specialized Construction Roles</h2>
<h3>Construction Estimator</h3>
<p>Prepares detailed cost estimates, material takeoffs, and bid proposals.</p>

<h3>BIM Modeler / CAD Drafter</h3>
<p>Creates 3D models and 2D shop drawings. Coordinates clash detection.</p>

<h3>Procurement Coordinator</h3>
<p>Sources materials, negotiates with vendors, and tracks delivery schedules.</p>`
    },
    {
        key: 'industry_education',
        name: 'Industries: Education',
        path: '/industries/education',
        defaultTitle: 'Education & EdTech Support',
        defaultDescription: 'Support for EdTech platforms and educational institutions. Tutors, grading assistants, and student success support.',
        defaultContent: `< h1 > Empowering Global Education</h1 >
<p><strong>Student success support, LMS administration, and remote tutoring teams.</strong></p>

<h2>Scalable Learning Support</h2>
<p>EdTech is booming, but student support often lags behind. We provide educated, empathetic staff to support learners of all ages.</p>
<p>From handling technical LMS issues to providing subject-matter tutoring in Math, Science, and English, our teams help you deliver a better learning experience at scale.</p>

<h2>EdTech Operations</h2>
<ul>
    <li><strong>Tutoring Services:</strong> Subject matter experts available for 1-on-1 or group coaching.</li>
    <li><strong>Student Success:</strong> Pro-active outreach to at-risk students to improve retention.</li>
    <li><strong>LMS Admin:</strong> Managing Canvas, Blackboard, or Moodle courses and enrollments.</li>
    <li><strong>Enrollment Sales:</strong> Admissions teams to guide prospective students through the funnel.</li>
</ul>

<h2>Education Roles</h2>
<h3>LMS Administrator</h3>
<p>Manages course uploads, user enrollments, and technical troubleshooting.</p>

<h3>Online Tutor</h3>
<p>Provides academic support and grading assistance.</p>

<h3>Admissions Counselor</h3>
<p>Handles inbound inquiries and guides applicants through enrollment.</p>`
    },
    {
        key: 'industry_engineering',
        name: 'Industries: Engineering',
        path: '/industries/engineering',
        defaultTitle: 'Engineering Services Outsourcing (ESO)',
        defaultDescription: 'Civil, Mechanical, and Electrical engineering support. CAD drafting and technical documentation.',
        defaultContent: `< h1 > Engineering Excellence</h1 >
<p><strong>Extend your engineering capabilities with specialized offshore technical talent.</strong></p>

<h2>Technical Precision</h2>
<p>Finding local engineering talent is expensive and competitive. We connect you with licensed engineers in the Philippines who can handle the technical heavy lifting.</p>
<p>Whether it's Civil, Structural, Mechanical, or Electrical, our teams integrate with your workflow to produce high-quality drawings, calculations, and reports.</p>

<h2>Engineering Support</h2>
<ul>
    <li><strong>Civil & Structural:</strong> Structural analysis, foundation design support, and drafting.</li>
    <li><strong>MEP Engineering:</strong> Mechanical, Electrical, and Plumbing design and coordination.</li>
    <li><strong>2D/3D CAD:</strong> Migration of legacy drawings to CAD/BIM.</li>
    <li><strong>Compliance:</strong> Ensuring designs meet international standards (ISO, ANSI).</li>
</ul>

<h2>Engineering Roles</h2>
<h3>Junior Engineer</h3>
<p>Supports senior staff with calculations, data analysis, and report generation.</p>

<h3>CAD Technician</h3>
<p>Drafting and detailing using AutoCAD, MicroStation, or SolidWorks.</p>

<h3>Technical Writer</h3>
<p>Creates operation manuals, specs, and technical documentation.</p>`
    },
    {
        key: 'industry_hr',
        name: 'Industries: HR',
        path: '/industries/hr',
        defaultTitle: 'HR & Recruitment Process Outsourcing (RPO)',
        defaultDescription: 'Global recruitment and HR support. Source candidates, manage payroll, and handle employee relations.',
        defaultContent: `< h1 > Global Talent Acquisition</h1 >
<p><strong>Scale your recruiting and HR operations with dedicated offshore teams.</strong></p>

<h2>Recruiting at Speed</h2>
<p>In a war for talent, speed is everything. Our Recruitment Process Outsourcing (RPO) teams act as an extension of your internal HR.</p>
<p>They source candidates, screen resumes, schedule interviews, and manage the onboarding process, allowing your HR leaders to focus on culture and strategy.</p>

<h2>HR Efficiency</h2>
<ul>
    <li><strong>Sourcing:</strong> Finding passive candidates on LinkedIn Recruiter and job boards.</li>
    <li><strong>Payroll Admin:</strong> Processing global payroll and benefits administration.</li>
    <li><strong>Employee Relations:</strong> Handling day-to-day HR inquiries and documentation.</li>
    <li><strong>Onboarding:</strong> Managing background checks and equipment provisioning.</li>
</ul>

<h2>HR Roles</h2>
<h3>Technical Recruiter</h3>
<p>Specialized in sourcing IT and engineering talent.</p>

<h3>HR Generalist</h3>
<p>Handles employee records, leave management, and policy compliance.</p>

<h3>Payroll Specialist</h3>
<p>Ensures accurate and timely payroll processing.</p>`
    },
    {
        key: 'industry_legal',
        name: 'Industries: Legal',
        path: '/industries/legal',
        defaultTitle: 'Legal Process Outsourcing (LPO)',
        defaultDescription: 'Paralegal and legal support services. Contract review, legal research, and transcription.',
        defaultContent: `< h1 > High - Value Legal Support</h1 >
<p><strong>Law firms and corporate counsel save 70% by offshoring legal processes.</strong></p>

<h2>Legal Efficiency</h2>
<p>Lawyers shouldn't bill $500/hour for document formatting. Legal Process Outsourcing (LPO) allows firms to delegate routine tasks to qualified legal professionals in the Philippines.</p>
<p>Our teams support litigation, real estate, and corporate law, ensuring accuracy and confidentiality.</p>

<h2>LPO Advantages</h2>
<ul>
    <li><strong>Legal Research:</strong> Conducting case law research and summarizing findings.</li>
    <li><strong>Contract Review:</strong> First-pass review of NDAs and standard agreements.</li>
    <li><strong>Litigation Support:</strong> Document coding, discovery support, and trial prep.</li>
    <li><strong>Transcription:</strong> Converting depositions and court hearings to text.</li>
</ul>

<h2>Legal Roles</h2>
<h3>Paralegal</h3>
<p>Drafts pleadings, manages case files, and communicates with clients.</p>

<h3>Legal Secretary</h3>
<p>Manages calendars, files court documents, and handles correspondence.</p>

<h3>Contract Administrator</h3>
<p>Manages contract lifecycles and database updates.</p>`
    },
    {
        key: 'industry_logistics',
        name: 'Industries: Logistics',
        path: '/industries/logistics',
        defaultTitle: 'Logistics & Supply Chain BPO',
        defaultDescription: 'Dispatch, freight forwarding, and inventory management support. 24/7 logistics operations.',
        defaultContent: `< h1 > Keep Your Supply Chain Moving</h1 >
<p><strong>24/7 dispatch and logistics coordination to ensure on-time delivery.</strong></p>

<h2>Logistics Never Sleeps</h2>
<p>In logistics, increased volume means increased noise. More calls, more emails, more tracking updates.</p>
<p>We build 'Control Tower' teams that monitor shipments, coordinate with drivers, and resolve issues in real-time. Whether you're a 3PL, a freight broker, or a vast trucking fleet, we keep your wheels turning.</p>

<h2>Supply Chain Support</h2>
<ul>
    <li><strong>Dispatch:</strong> Coordinating loads and drivers 24/7.</li>
    <li><strong>Freight Brokerage:</strong> Covering loads and negotiating rates with carriers.</li>
    <li><strong>Track & Trace:</strong> Pro-active monitoring of shipment status.</li>
    <li><strong>Back Office:</strong> Bill of Lading (BOL) processing and invoicing.</li>
</ul>

<h2>Logistics Roles</h2>
<h3>Logistics Coordinator</h3>
<p>Manages end-to-end shipment lifecycle.</p>

<h3>Dispatcher</h3>
<p>Assigns drivers to routes and monitors performance.</p>

<h3>Import/Export Clerk</h3>
<p>Handles customs documentation and compliance.</p>`
    },
    {
        key: 'industries_manufacturing',
        name: 'Industries: Manufacturing',
        path: '/industries/manufacturing',
        defaultTitle: 'Manufacturing & Production Support',
        defaultDescription: 'Production planning, QA, and supply chain support for manufacturers.',
        defaultContent: `< h1 > Lean Manufacturing Support</h1 >
<p><strong>Back-office efficiency for the factory floor.</strong></p>

<h2>Optimize Production</h2>
<p>Manufacturing isn't just about machines; it's about data. Inventory levels, production schedules, quality reports.</p>
<p>We support manufacturers by handling the data-heavy side of operations. From BOM (Bill of Materials) management to production planning, we ensure your factory runs on schedule.</p>

<h2>Factory Support</h2>
<ul>
    <li><strong>Production Planning:</strong> Scheduling runs based on demand forecasts.</li>
    <li><strong>Inventory Mgmt:</strong> Tracking raw materials and finished goods.</li>
    <li><strong>Maintenance Admin:</strong> Scheduling equipment maintenance and tracking parts.</li>
    <li><strong>QA Documentation:</strong> Logging quality checks and compliance reports.</li>
</ul>

<h2>Manufacturing Roles</h2>
<h3>Production Planner</h3>
<p>Balances demand with capacity to create efficient schedules.</p>

<h3>Supply Chain Analyst</h3>
<p>Optimizes procurement and inventory levels.</p>

<h3>BOM Specialist</h3>
<p>Maintains accurate Bill of Materials records.</p>`
    },
    {
        key: 'industries_marketing',
        name: 'Industries: Marketing',
        path: '/industries/marketing',
        defaultTitle: 'Marketing & Advertising BPO',
        defaultDescription: 'Creative design, SEO, and performance marketing teams. Scale your agency or internal team.',
        defaultContent: `< h1 > Scale Your Creative Output</h1 >
<p><strong>Graphic design, content writing, and digital marketing execution at scale.</strong></p>

<h2>Creative at Speed</h2>
<p>Content is king, but producing it is expensive. Agencies and brands struggle to keep up with the demand for social posts, blogs, and ad creatives.</p>
<p>We provide creative pods—designers, writers, and editors—who work in your time zone to deliver high-quality assets. Scale your production without burning out your local team.</p>

<h2>Marketing Ops</h2>
<ul>
    <li><strong>Social Media:</strong> Scheduling posts, community management, and graphic design.</li>
    <li><strong>PPC Management:</strong> Campaign setup, bid management, and reporting.</li>
    <li><strong>SEO:</strong> On-page optimization, link building, and keyword research.</li>
    <li><strong>Email Marketing:</strong> Campaign layout, segmentation, and deployment.</li>
</ul>

<h2>Marketing Roles</h2>
<h3>Graphic Designer</h3>
<p>Creates social assets, banners, and ebooks.</p>

<h3>Content Writer</h3>
<p>Writes SEO-optimized blog posts and web copy.</p>

<h3>Marketing Coordinator</h3>
<p>Keeps campaigns on track and manages calendars.</p>`
    },
    {
        key: 'industries_media',
        name: 'Industries: Media',
        path: '/industries/media',
        defaultTitle: 'Media & Entertainment Services',
        defaultDescription: 'Video editing, animation, and post-production support for creators and studios.',
        defaultContent: `< h1 > Post - Production Powerhouse</h1 >
<p><strong>Video editing, sound engineering, and visual effects.</strong></p>

<h2>Content Production</h2>
<p>The creator economy runs on video. But editing is a bottleneck.</p>
<p>Our post-production teams use Adobe Premiere, After Effects, and DaVinci Resolve to turn raw footage into polished content. Whether you're a YouTuber or a production house, we help you publish faster.</p>

<h2>Production Support</h2>
<ul>
    <li><strong>Video Editing:</strong> Cutting, color grading, and sound mixing.</li>
    <li><strong>Animation:</strong> 2D/3D animation and motion graphics.</li>
    <li><strong>Audio Engineering:</strong> Podcast editing and sound design.</li>
    <li><strong>Subtitling:</strong> Transcription and closed captioning for accessibility.</li>
</ul>

<h2>Media Roles</h2>
<h3>Video Editor</h3>
<p>End-to-end post-production for social and long-form video.</p>

<h3>Motion Graphics Artist</h3>
<p>Creates intros, lower thirds, and explainers.</p>

<h3>Thumbnail Artist</h3>
<p>Designs high-CTR thumbnails for YouTube.</p>`
    },
    {
        key: 'industries_ops',
        name: 'Industries: Operations',
        path: '/industries/operations',
        defaultTitle: 'Operations & Process Support',
        defaultDescription: 'Back-office optimization. Data entry, executive assistants, and process management.',
        defaultContent: `< h1 > Operational Excellence</h1 >
<p><strong>Streamline your back office and free up your core team.</strong></p>

<h2>The Backbone of Business</h2>
<p>Every business has grunt work. Data entry, scheduling, file management. It's necessary, but it doesn't drive revenue.</p>
<p>We provide the operational backbone to keep your business running smoothly. Our process associates handle the details so your leadership can focus on the big picture.</p>

<h2>Ops Support</h2>
<ul>
    <li><strong>Data Entry:</strong> Fast, accurate data processing and cleansing.</li>
    <li><strong>Executive Assistants:</strong> Calendar management, travel booking, and email triage.</li>
    <li><strong>Research:</strong> Market research and lead enrichment.</li>
    <li><strong>Quality Assurance:</strong> Auditing calls and processes for compliance.</li>
</ul>

<h2>Operations Roles</h2>
<h3>Executive Assistant (EA)</h3>
<p>The right hand to your CEO. Specialized in time management.</p>

<h3>Data Entry Specialist</h3>
<p>High-speed typing and attention to detail.</p>

<h3>Process Associate</h3>
<p>Follows SOPs to execute routine business processes.</p>`
    },
    {
        key: 'industries_realestate',
        name: 'Industries: Real Estate',
        path: '/industries/real-estate',
        defaultTitle: 'Real Estate Virtual Assistants',
        defaultDescription: 'Support for realtors, property managers, and investors. ISAs, transaction coordinators, and admin.',
        defaultContent: `< h1 > Scale Your Portfolio</h1 >
<p><strong>Property management and real estate support to help you close more deals.</strong></p>

<h2>Real Estate Efficiency</h2>
<p>Top producers don't handle their own paperwork. They focus on closing.</p>
<p>We provide specialized Real Estate Virtual Assistants (REVAs) who understand the industry. From cold calling leads (ISA) to managing transaction checklists, we help you sell more homes.</p>

<h2>REVA Support</h2>
<ul>
    <li><strong>Inside Sales (ISA):</strong> Cold calling and qualifying leads for agents.</li>
    <li><strong>Property Mgmt:</strong> Handling tenant inquiries and maintenance requests.</li>
    <li><strong>Transaction Coord:</strong> Managing the paperwork from contract to close.</li>
    <li><strong>Marketing:</strong> Creating flyers, listing descriptions, and social posts.</li>
</ul>

<h2>Real Estate Roles</h2>
<h3>Inside Sales Agent (ISA)</h3>
<p>Prospects for new leads and sets appointments.</p>

<h3>Transaction Coordinator</h3>
<p>Ensures all documents are signed and deadlines met.</p>

<h3>Property Manager Assistant</h3>
<p>Coordinates repairs and communicates with tenants.</p>`
    },
    {
        key: 'industries_sales',
        name: 'Industries: Sales',
        path: '/industries/sales',
        defaultTitle: 'Sales & Business Development',
        defaultDescription: 'Outbound sales teams, SDRs, and appointment setters. Fill your pipeline with qualified leads.',
        defaultContent: `< h1 > Fill Your Pipeline</h1 >
<p><strong>Dedicated Sales Development Reps (SDRs) to prospect, qualify, and book meetings.</strong></p>

<h2>Outbound Growth</h2>
<p>Cold calling is hard. But it works. The problem is consistency.</p>
<p>We build dedicated outbound sales teams that do nothing but prospect. They use tools like Apollo, ZoomInfo, and Salesforce to find decision-makers and book demos for your closers.</p>

<h2>Sales Development</h2>
<ul>
    <li><strong>Cold Calling:</strong> High-volume outbound calling to set appointments.</li>
    <li><strong>Lead Generation:</strong> Building verified lists of ideal prospects.</li>
    <li><strong>Account Mgmt:</strong> Nurturing existing client relationships.</li>
    <li><strong>Sales Ops:</strong> CRM hygiene and reporting.</li>
</ul>

<h2>Sales Roles</h2>
<h3>SDR / BDR</h3>
<p>Focuses 100% on generating new opportunities.</p>

<h3>Lead Researcher</h3>
<p>Finds contact info and enriches data.</p>

<h3>Account Manager</h3>
<p>Handles renewals and upsells for existing accounts.</p>`
    },
    {
        key: 'industries_travel',
        name: 'Industries: Travel',
        path: '/industries/travel',
        defaultTitle: 'Travel & Hospitality BPO',
        defaultDescription: '24/7 travel support for agencies and hotels. Reservations, customer service, and concierge.',
        defaultContent: `< h1 > World - Class Hospitality</h1 >
<p><strong>24/7 support for travel agencies, airlines, and hotels.</strong></p>

<h2>The Traveler's Companion</h2>
<p>Travel disrupts schedules. Flights get cancelled. Plans change. Your customers need support 24/7.</p>
<p>We provide travel support teams that handle reservations, re-booking, and concierge services. With excellent English and a service-first mindset, we ensure your travelers feel taken care of.</p>

<h2>Travel Ops</h2>
<ul>
    <li><strong>Reservations:</strong> Booking flights, hotels, and tours.</li>
    <li><strong>Ticketing:</strong> Handling changes, cancellations, and refunds.</li>
    <li><strong>Concierge:</strong> Planning itineraries and making dining reservations.</li>
    <li><strong>24/7 Support:</strong> Emergency assistance for travelers.</li>
</ul>

<h2>Hospitality Roles</h2>
<h3>Travel Agent</h3>
<p>Certified agents familiar with GDS (Sabre, Amadeus).</p>

<h3>Booking Coordinator</h3>
<p>Manages reservations and vendor confirmations.</p>

<h3>Guest Services</h3>
<p>Handles inquiries and complaints for hotels/rentals.</p>`
    },

    // Resources
    {
        key: 'resources',
        name: 'Resources (Main)',
        path: '/resources',
        defaultTitle: 'BPO Resources | Case Studies, Blog, Calculators',
        defaultDescription: 'Access free BPO resources including ROI calculators, case studies, industry glossaries, and expert insights on outsourcing.',
        defaultContent: `< h1 > BPO Resources</h1 > <p>Explore our library of resources designed to help you succeed in outsourcing.</p>`
    },
    {
        key: 'case_studies',
        name: 'Case Studies',
        path: '/case-studies',
        defaultTitle: 'BPO Case Studies | Success Stories',
        defaultDescription: 'Read how BPO Consultants helped businesses scale and save costs.',
        defaultContent: `< h1 > Client Success Stories</h1 > <p>Discover how businesses like yours have transformed their operations with BPO Consultants.</p>`
    },
    {
        key: 'calculator',
        name: 'ROI Calculator',
        path: '/calculator',
        defaultTitle: 'BPO Cost Savings Calculator | ROI Analysis',
        defaultDescription: 'Calculate your potential savings with our interactive BPO ROI calculator.',
        defaultContent: `< h1 > ROI Calculator</h1 > <p>Use our calculator to estimate your potential savings by outsourcing with BPO Consultants.</p>`
    },
    {
        key: 'glossary',
        name: 'Glossary (Main)',
        path: '/glossary',
        defaultTitle: 'BPO Glossary | Industry Terms Explained',
        defaultDescription: 'Comprehensive glossary of BPO industry terms and definitions.',
        defaultContent: `< h1 > BPO Industry Glossary</h1 > <p>A comprehensive guide to common terms and acronyms used in the BPO industry.</p>`
    },
    {
        key: 'blog',
        name: 'Blog (Main)',
        path: '/blog',
        defaultTitle: 'BPO Insights Blog | Industry Trends & News',
        defaultDescription: 'Latest news, trends, and insights from the world of business process outsourcing.',
        defaultContent: `< h1 > Insights & News</h1 > <p>Stay updated with the latest trends, tips, and news from the world of outsourcing.</p>`
    },

    // Training Hub
    {
        key: 'training',
        name: 'Training Hub',
        path: '/training',
        defaultTitle: 'BPO Training Academy | Free Courses & Certifications',
        defaultDescription: 'Upskill your team with our free training programs. From Foundation to Leadership, we build world-class talent.',
        defaultContent: `< h1 > BPO Training Hub</h1 > <p>Empowering your team with world-class training and development programs.</p>`
    },
    {
        key: 'training_webinars',
        name: 'Training: Webinars',
        path: '/training/webinars',
        defaultTitle: 'Webinars & Events | BPO Consultants',
        defaultDescription: 'Join our live sessions on BPO trends, AI in sales, and global staffing strategies.',
        defaultContent: `< h1 > Webinars & Live Events</h1 > <p>Join our experts for live sessions on the latest in BPO, AI, and business strategy.</p>`
    },
    {
        key: 'training_foundation',
        name: 'Training: BPO Foundation',
        path: '/training/foundation',
        defaultTitle: 'BPO Foundation Course | BPO Consultants',
        defaultDescription: 'Master the basics of the BPO industry. Learn about call center operations, KPIs, and cultural nuances.',
        defaultContent: `<h1>BPO Foundation</h1><p>Start your career with a solid understanding of BPO operations and metrics.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "BPO Foundation Course",
            "description": "Essential starting point for a career in the BPO industry.",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'training_communication',
        name: 'Training: Communication',
        path: '/training/communication',
        defaultTitle: 'Advanced Communication & Culture Training | BPO Consultants',
        defaultDescription: 'Master Western business culture and neutralize your accent. Advanced communication skills for BPO professionals.',
        defaultContent: `<h1>Advanced Communication</h1><p>Bridge the cultural gap and communicate with confidence in a global environment.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Advanced Communication & Culture",
            "description": "Master Western business culture and neutralized accent training.",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'training_technical',
        name: 'Training: Technical Skills',
        path: '/training/technical',
        defaultTitle: 'Technical Skills & Tools Training | BPO Consultants',
        defaultDescription: 'Learn the software that powers the BPO industry. Salesforce, Zendesk, QuickBooks, and more.',
        defaultContent: `<h1>Technical Skills</h1><p>Master the tools and software that top BPO employers require.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Technical Skills & Tools",
            "description": "Master industry-standard software and tools used in BPO.",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },
    {
        key: 'training_leadership',
        name: 'Training: Leadership',
        path: '/training/leadership',
        defaultTitle: 'BPO Leadership Academy | BPO Consultants',
        defaultDescription: 'Training for Team Leaders and Operations Managers. Coaching, performance management, and KPI analysis.',
        defaultContent: `<h1>Leadership Academy</h1><p>Develop the skills to lead high-performance teams in the BPO industry.</p>`,
        defaultSchema: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "BPO Leadership Academy",
            "description": "Advanced training for BPO team leaders and managers.",
            "provider": {
                "@type": "Organization",
                "name": "BPO Consultants"
            }
        }, null, 4)
    },

    // AI & BD Training
    {
        key: 'training_aibd',
        name: 'Training: AI & BD (Main)',
        path: '/training/ai-bd',
        defaultTitle: 'AI-Augmented BD Training | BPO Consultants',
        defaultDescription: 'Master the future of business development with our AI-Augmented curriculum.',
        defaultContent: `< h1 > AI - Augmented Business Development</h1 > <p>Master the intersection of AI and sales to drive unprecedented growth.</p>`
    },
    {
        key: 'training_aibd_prompt',
        name: 'Training: Prompt Engineering',
        path: '/training/ai-bd/prompt-engineering',
        defaultTitle: 'Prompt Engineering for Sales Training | BPO Consultants',
        defaultDescription: 'Master ChatGPT and Claude for sales. Learn to write high-converting prompts, automate scripts, and handle objections with AI.',
        defaultContent: `< h1 > Prompt Engineering for Sales</h1 > <p>Learn how to craft effective prompts to leverage AI in your sales process.</p>`
    },
    {
        key: 'training_aibd_scoring',
        name: 'Training: Lead Scoring',
        path: '/training/ai-bd/lead-scoring',
        defaultTitle: 'Automated Lead Scoring Training | BPO Consultants',
        defaultDescription: 'Learn to use predictive analytics to qualify leads. Master data enrichment and intent data for higher conversion rates.',
        defaultContent: `< h1 > Automated Lead Scoring</h1 > <p>Implement data-driven lead scoring models to prioritize your best prospects.</p>`
    },
    {
        key: 'training_aibd_personalization',
        name: 'Training: Personalization',
        path: '/training/ai-bd/personalization',
        defaultTitle: 'AI-Driven Personalization Training | BPO Consultants',
        defaultDescription: 'Scale your outreach without losing the human touch. Learn to use AI for hyper-personalized emails and video messages.',
        defaultContent: `< h1 > AI - Driven Personalization</h1 > <p>Scale your outreach with hyper-personalized content generated by AI.</p>`
    },
    {
        key: 'training_aibd_crm',
        name: 'Training: CRM Automation',
        path: '/training/ai-bd/crm-automation',
        defaultTitle: 'CRM Integration & Automation Training | BPO Consultants',
        defaultDescription: 'Streamline your sales ops. best practices for Zapier, Make.com, and HubSpot automation.',
        defaultContent: `< h1 > CRM Automation</h1 > <p>Automate your sales workflows and keep your CRM data clean and actionable.</p>`
    },

    // Experienced BD Training
    {
        key: 'training_exp',
        name: 'Training: Experienced BD (Main)',
        path: '/training/experienced-bd',
        defaultTitle: 'Experienced BD Track | BPO Consultants',
        defaultDescription: 'Advanced training for seasoned sales professionals targeting international markets.',
        defaultContent: `< h1 > Experienced BD Track</h1 > <p>Advanced strategies and tactics for seasoned business development professionals.</p>`
    },
    {
        key: 'training_exp_mapping',
        name: 'Training: Account Mapping',
        path: '/training/experienced-bd/account-mapping',
        defaultTitle: 'Enterprise Account Mapping Training | BPO Consultants',
        defaultDescription: 'Master the art of navigating complex organizations. Learn to potential champions, uncover blockers, and strategic account planning.',
        defaultContent: `< h1 > Enterprise Account Mapping</h1 > <p>Navigate complex organizations and identify key stakeholders effectively.</p>`
    },
    {
        key: 'training_exp_stakeholder',
        name: 'Training: Stakeholder Mgmt',
        path: '/training/experienced-bd/stakeholder-management',
        defaultTitle: 'Stakeholder Management Training | BPO Consultants',
        defaultDescription: 'Learn to identify and influence the buying committee. Turn blockers into champions and secure consensus.',
        defaultContent: `< h1 > Stakeholder Management</h1 > <p>Strategies for influencing decision-makers and aligning stakeholders.</p>`
    },
    {
        key: 'training_exp_negotiation',
        name: 'Training: Negotiation Tactics',
        path: '/training/experienced-bd/negotiation-tactics',
        defaultTitle: 'Advanced Sales Negotiation Training | BPO Consultants',
        defaultDescription: 'Master contract negotiations. Learn to defend pricing, handle procurement, and trade concessions intelligently.',
        defaultContent: `< h1 > Advanced Negotiation Tactics</h1 > <p>Master the art of negotiation to close deal with better terms.</p>`
    },
    {
        key: 'training_exp_closing',
        name: 'Training: Closing Deals',
        path: '/training/experienced-bd/closing-deals',
        defaultTitle: 'Closing Six-Figure Deals Training | BPO Consultants',
        defaultDescription: 'Learn the mechanics of closing large enterprise contracts. Procurement navigation, legal reviews, and final sign-off.',
        defaultContent: `< h1 > Closing Major Deals</h1 > <p>Techniques and strategies for bringing large enterprise deals to a successful close.</p>`
    },

    // Aspiring BD Training
    {
        key: 'training_aspiring',
        name: 'Training: Aspiring BD (Main)',
        path: '/training/aspiring-bd',
        defaultTitle: 'Aspiring BD Track | BPO Consultants',
        defaultDescription: 'Launch your career in Business Development. Essential skills for new sales professionals.',
        defaultContent: `< h1 > Aspiring BD Track</h1 > <p>Foundational training for those looking to start a career in business development.</p>`
    },
    {
        key: 'training_aspiring_funnel',
        name: 'Training: Sales Funnel',
        path: '/training/aspiring-bd/sales-funnel',
        defaultTitle: 'Understanding the Sales Funnel Training | BPO Consultants',
        defaultDescription: 'Learn the fundamentals of B2B sales. Understand MQLs, SQLs, and how to move prospects through the AIDA funnel.',
        defaultContent: `< h1 > Understanding the Sales Funnel</h1 > <p>Learn the basics of the sales funnel and how to move prospects through each stage.</p>`
    },
    {
        key: 'training_aspiring_outreach',
        name: 'Training: Cold Outreach',
        path: '/training/aspiring-bd/cold-outreach',
        defaultTitle: 'Cold Email & Calling Basics Training | BPO Consultants',
        defaultDescription: 'Conquer cold calling anxiety. Learn proven scripts for phone and email outreach that get replies.',
        defaultContent: `< h1 > Cold Outreach Basics</h1 > <p>Master the fundamentals of cold calling and emailing to generate new opportunities.</p>`
    },
    {
        key: 'training_aspiring_objection',
        name: 'Training: Objection Handling',
        path: '/training/aspiring-bd/objection-handling',
        defaultTitle: 'Handling Sales Objections Training | BPO Consultants',
        defaultDescription: 'Turn \'No\' into \'Maybe\'. Master the LAER framework to handle objections about price, timing, and competitors.',
        defaultContent: `< h1 > Objection Handling</h1 > <p>Learn how to effectively overcome common sales objections and keep the conversation moving.</p>`
    },
    {
        key: 'training_aspiring_comm',
        name: 'Training: Prof. Comm',
        path: '/training/aspiring-bd/professional-communication',
        defaultTitle: 'Professional Communication Training | BPO Consultants',
        defaultDescription: 'Polish your business persona. Master active listening, email etiquette, and executive presence on Zoom.',
        defaultContent: `< h1 > Professional Communication</h1 > <p>Develop the communication skills necessary for success in a professional business environment.</p>`
    },

    // Legal
    {
        key: 'privacy',
        name: 'Legal: Privacy Policy',
        path: '/privacy-policy',
        defaultTitle: 'Privacy Policy | BPO Consultants',
        defaultDescription: 'Our commitment to protecting your data.',
        defaultContent: `< h1 > Privacy Policy</h1 >
<p>Last updated: ` + new Date().toLocaleDateString() + `</p>
<p>At BPO Consultants, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information.</p>

<h2>1. Information We Collect</h2>
<p>We collect information you provide directly to us, such as when you fill out a contact form, sign up for a newsletter, or register for a course. This may include your name, email address, phone number, and company name.</p>

<h2>2. How We Use Your Information</h2>
<p>We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to personalize your experience.</p>

<h2>3. Data Security</h2>
<p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.</p>

<h2>4. Contact Us</h2>
<p>If you have any questions about this Privacy Policy, please contact us at info@bpoconsultants.ph.</p>`
    },
    {
        key: 'terms',
        name: 'Legal: Terms of Service',
        path: '/terms-of-service',
        defaultTitle: 'Terms of Service | BPO Consultants',
        defaultDescription: 'Rules and regulations for using our website.',
        defaultContent: `< h1 > Terms of Service</h1 >
<p>Last updated: ` + new Date().toLocaleDateString() + `</p>
<p>Please read these Terms of Service carefully before using our website operated by BPO Consultants.</p>

<h2>1. Conditions of Use</h2>
<p>By using this website, you certify that you have read and reviewed this Agreement and that you agree to comply with its terms. If you do not want to be bound by the terms of this Agreement, you are advised to leave the website accordingly.</p>

<h2>2. Intellectual Property</h2>
<p>You agree that all materials, products, and services provided on this website are the property of BPO Consultants, its affiliates, directors, officers, employees, agents, suppliers, or licensors including all copyrights, trade secrets, trademarks, patents, and other intellectual property.</p>

<h2>3. User Accounts</h2>
<p>As a user of this website, you may be asked to register with us and provide private information. You are responsible for ensuring the accuracy of this information, and you are responsible for maintaining the safety and security of your identifying information.</p>

<h2>4. Limitation of Liability</h2>
<p>BPO Consultants is not liable for any damages that may occur to you as a result of your misuse of our website.</p>`
    },
    {
        key: 'cookie',
        name: 'Legal: Cookie Policy',
        path: '/cookie-policy',
        defaultTitle: 'Cookie Policy | BPO Consultants',
        defaultDescription: 'How we use cookies to improve your experience.',
        defaultContent: `< h1 > Cookie Policy</h1 >
<p>Last updated: ` + new Date().toLocaleDateString() + `</p>
<p>This Cookie Policy explains how BPO Consultants uses cookies and similar technologies to recognize you when you visit our website.</p>

<h2>1. What are cookies?</h2>
<p>Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.</p>

<h2>2. Why do we use cookies?</h2>
<p>We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties.</p>

<h2>3. How can I control cookies?</h2>
<p>You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager.</p>`
    },
    {
        key: 'gdpr',
        name: 'Legal: GDPR Compliance',
        path: '/gdpr-compliance',
        defaultTitle: 'GDPR Compliance | BPO Consultants',
        defaultDescription: 'Our compliance with the General Data Protection Regulation.',
        defaultContent: `< h1 > GDPR Compliance</h1 >
<p>BPO Consultants is committed to compliance with the General Data Protection Regulation (GDPR).</p>

<h2>1. Data Subject Rights</h2>
<p>If you are a resident of the European Economic Area (EEA), you have certain data protection rights. BPO Consultants aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.</p>

<h2>2. Legal Basis for Processing</h2>
<p>If you are from the European Economic Area (EEA), BPO Consultants' legal basis for collecting and using the personal information described in this Privacy Policy depends on the Personal Data we collect and the specific context in which we collect it.</p>

<h2>3. Data Retention</h2>
<p>BPO Consultants will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy.</p>`
    },
];

const SEOManager = () => {
    const [selectedPage, setSelectedPage] = useState(PAGES[0]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: '',
        content: '',
        isCrawlable: true,
        schemaMarkup: ''
    });
    const [showGlobalSettings, setShowGlobalSettings] = useState(false);
    const [globalIsCrawlable, setGlobalIsCrawlable] = useState(true);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const fetchPageData = useCallback(async () => {
        if (!selectedPage) return;

        setLoading(true);
        try {
            const docRef = doc(db, 'page_meta', selectedPage.key);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData({
                    title: data.title || '',
                    description: data.description || '',
                    image: data.image || '',
                    content: data.content || selectedPage.defaultContent || '',
                    isCrawlable: data.isCrawlable !== false, // Default to true if undefined
                    schemaMarkup: data.schemaMarkup || selectedPage.defaultSchema || ''
                });
            } else {
                setFormData({
                    title: selectedPage.defaultTitle || '',
                    description: selectedPage.defaultDescription || '',
                    image: '',
                    content: selectedPage.defaultContent || '',
                    isCrawlable: true,
                    schemaMarkup: selectedPage.defaultSchema || ''
                });
            }
        } catch (error) {
            console.error("Error fetching SEO data:", error);
            // alert("Failed to load SEO data for this page."); 
        } finally {
            setLoading(false);
        }
    }, [selectedPage]);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await setDoc(doc(db, 'page_meta', selectedPage.key), formData);
            setMessage('SEO Settings Saved Successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error saving SEO data:", error);
            setMessage('Error saving data.');
        }
        setLoading(false);
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1>SEO Manager <span>.</span></h1>
                </div>
                <div className="page-hdr-right flex items-center gap-3">
                    <button
                        onClick={() => setShowGlobalSettings(!showGlobalSettings)}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <Globe size={16} />
                        Global Settings
                    </button>
                </div>
            </div>

            {showGlobalSettings && (
                <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Global SEO Settings</h3>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-gray-900">Search Engine Indexing</p>
                            <p className="text-sm text-gray-500">Allow search engines like Google to show this site in search results.</p>
                        </div>
                        <div className="flex items-center">
                            <button
                                onClick={async () => {
                                    const newValue = !globalIsCrawlable;
                                    setGlobalIsCrawlable(newValue);
                                    try {
                                        await setDoc(doc(db, 'page_meta', 'global_settings'), { isCrawlable: newValue });
                                        setMessage('Global settings updated!');
                                        setTimeout(() => setMessage(''), 3000);
                                    } catch (err) {
                                        console.error(err);
                                        setMessage('Error saving global settings');
                                    }
                                }}
                                className={`relative inline - flex h - 6 w - 11 flex - shrink - 0 cursor - pointer rounded - full border - 2 border - transparent transition - colors duration - 200 ease -in -out focus: outline - none ${globalIsCrawlable ? 'bg-[#A2CB52]' : 'bg-gray-200'} `}
                            >
                                <span className={`pointer - events - none inline - block h - 5 w - 5 transform rounded - full bg - white shadow ring - 0 transition duration - 200 ease -in -out ${globalIsCrawlable ? 'translate-x-5' : 'translate-x-0'} `} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
                {/* Sidebar Page List - Sticky & Scrollable */}
                <div className="col-span-1">
                    <div className="sticky top-4 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col max-h-[calc(100vh-200px)]">
                        <div className="p-3 bg-gray-50 border-b border-gray-200 font-medium text-sm text-gray-500 shrink-0">
                            Select Page
                        </div>
                        <div className="divide-y divide-gray-100 overflow-y-auto">
                            {PAGES.map(page => (
                                <button
                                    key={page.key}
                                    onClick={() => setSelectedPage(page)}
                                    className={`w - full text - left px - 4 py - 3 text - sm flex items - center justify - between hover: bg - gray - 50 transition - colors ${selectedPage.key === page.key ? 'bg-[#ECF5DC] text-[#20222D] font-medium' : 'text-gray-700'} `}
                                >
                                    <span className="truncate">{page.name}</span>
                                    {selectedPage.key === page.key && <div className="w-1.5 h-1.5 rounded-full bg-[#A2CB52] shrink-0 ml-2"></div>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Edit Form */}
                <div className="col-span-3">
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{selectedPage.name}</h2>
                                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                    <Globe size={12} /> {selectedPage.path}
                                </p>
                            </div>
                            {message && (
                                <span className={`text - sm px - 3 py - 1 rounded - full ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'} `}>
                                    {message}
                                </span>
                            )}
                        </div>

                        <form onSubmit={handleSave} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Page Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#A2CB52] focus:border-[#A2CB52] text-gray-900"
                                    placeholder={`e.g.${selectedPage.name} - BPO Consultants PH`}
                                />
                                <p className="mt-1 text-xs text-gray-500">Recommended length: 50-60 characters</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Meta Description</label>
                                <textarea
                                    rows="3"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#A2CB52] focus:border-[#A2CB52] text-gray-900"
                                    placeholder="Brief description of the page content for search engines..."
                                />
                                <p className="mt-1 text-xs text-gray-500">Recommended length: 150-160 characters</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Social Share Image (URL)</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-grow">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <ImageIcon size={16} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="url"
                                            value={formData.image}
                                            onChange={e => setFormData({ ...formData, image: e.target.value })}
                                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </div>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">Image to display when shared on Facebook, Twitter, LinkedIn.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Page Content (Optional)</label>
                                <textarea
                                    rows="10"
                                    value={formData.content || ''}
                                    onChange={e => setFormData({ ...formData, content: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#A2CB52] focus:border-[#A2CB52] text-gray-900 font-mono text-sm"
                                    placeholder="You can use this field to store/edit the main content text for this page..."
                                />
                                <p className="mt-1 text-xs text-gray-500">HTML or plain text content for the page body.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Search Engine Visibility</label>
                                    <div className="flex items-center mt-2">
                                        <input
                                            type="checkbox"
                                            id="isCrawlable"
                                            checked={formData.isCrawlable}
                                            onChange={e => setFormData({ ...formData, isCrawlable: e.target.checked })}
                                            className="h-4 w-4 text-[#A2CB52] focus:ring-[#A2CB52] border-gray-300 rounded"
                                        />
                                        <label htmlFor="isCrawlable" className="ml-2 block text-sm text-gray-900">
                                            Allow search engines to index this page
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Schema Markup (JSON-LD)</label>
                                    <textarea
                                        rows="5"
                                        value={formData.schemaMarkup}
                                        onChange={e => setFormData({ ...formData, schemaMarkup: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#A2CB52] focus:border-[#A2CB52] text-gray-900 font-mono text-xs"
                                        placeholder='{ "@context": "https://schema.org", ... }'
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Valid JSON-LD only.</p>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-100 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#A2CB52] hover:bg-[#91b849] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A2CB52] disabled:opacity-50"
                                >
                                    <Save size={16} className="mr-2" />
                                    {loading ? 'Saving...' : 'Save Meta Tags'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SEOManager;
